import { inverseRecord } from "@base/util/TypeUtil";
import { ArticleAvailability, OfferListType } from "@biz/definitions/ArticleDefinitions";
import { INotesAndFiles } from "@biz/definitions/ProductDefinitions";
import {
  ConcavityType, CornerType, EdgeName, gewindeStangenArticleIds, ImageSource, IMaterialSettings, ISvgMeasureOptions, MaterialSettingIds, materialSettings, PointName, PoleId, PoleIndex, ringschraubenArticleIds, sailTypeByShortName, sailTypesMap,
  SunSailNetPoints, SunSailNetSides, SunSailSides, SunSailType, SunSailTypeNameShort, UserFileImageSource
} from "@biz/definitions/SunSailDefinitions";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { ILocaContext, LocaContext } from "@biz/language/LocaContext";

export function def(obj: any): string {
  return obj; // not needed anymore (ensure that esModule: true is set for server, dash, app_vite, sc...
  /*if (!obj)
    return "";
  if (typeof obj === "string")
    return obj;
  if (typeof obj === "object" && "default" in obj)
    return obj.default;
  return obj;*/

}

export function hasPoleId(poleId: PoleId | undefined): poleId is number {
  return (poleId !== undefined && poleId !== -1);
}

export function hasEdgeName(edgeName: EdgeName | undefined): edgeName is EdgeName {
  return !!edgeName;
}

export function eqPoleIndex(poleIndex: number | undefined, otherPoleIndex: number | undefined): boolean {
  const hasA = hasPoleId(poleIndex);
  const hasB = hasPoleId(otherPoleIndex);
  if (hasA !== hasB)
    return false;
  if (!hasA && !hasB)
    return true;
  return hasA && hasB && poleIndex === otherPoleIndex;
}

export function eqEdgeName(edgeName: EdgeName | undefined, otherEdgeName: EdgeName | "none" | undefined): boolean {
  const hasA = !!edgeName;
  const hasB = !!otherEdgeName && otherEdgeName !== "none";
  if (hasA !== hasB)
    return false;
  if (!hasA && !hasB)
    return true;
  return hasA && hasB && edgeName === otherEdgeName;
}

export function getSunSailNameShort(system: SunSailType): SunSailTypeNameShort {
  return sailTypesMap[system]?.shortName || "Unknown";
}

export function getSunSailName(system: SunSailType): string {
  return sailTypesMap[system]?.name || "Unknown";
}

export function getCornerTypeName(cornerType: CornerType | undefined, lc: ILocaContext): string {
  if (cornerType === undefined)
    cornerType = CornerType.None;
  return lc.t(LocaContext.connect("CornerType", cornerType));
}

export function getLetterForNumber(n: number, startLetter: string = "A"): string {
  return String.fromCharCode(startLetter.charCodeAt(0) + n);
}

export function getPointName(poleId: PoleId): PointName {
  const pid = Poles.fromPoleId(poleId);

  if (pid.polePositionType === "center")
    return `📍${pid.poleIndex + 1}` as PointName;
  return getLetterForNumber(pid.poleIndex, "A") as PointName;
}

export function getPointNameSmall(poleId: PoleId): SunSailNetPoints {
  const pid = Poles.fromPoleId(poleId);

  if (pid.polePositionType === "center")
    return `📍${pid.poleIndex + 1}` as SunSailNetPoints;
  return getLetterForNumber(pid.poleIndex, "a") as SunSailNetPoints;
}

export function getSideName(aIndex: PoleId, bIndex: PoleId, sort: boolean = true): SunSailSides {
  if (sort && aIndex > bIndex) {
    [aIndex, bIndex] = [bIndex, aIndex];
  }
  return (getPointName(aIndex) + getPointName(bIndex)) as SunSailSides;
}

export function getEdgeName(aIndex: PoleId, bIndex: PoleId, sort: boolean = true): EdgeName {
  return getSideNameSmall(aIndex, bIndex, sort);
}

export function getEdgeNameFromSunSailSide(sunSailSide: SunSailSides): EdgeName {
  return sunSailSide.toLocaleLowerCase() as EdgeName;
}

export function getSideDisplayName(edge: SunSailSides | EdgeName): string {
  return edge[0] + "-" + edge[1]
}

export function getSideNameSmall(aIndex: PoleId, bIndex: PoleId, sort: boolean = true): SunSailNetSides {
  if (sort && aIndex > bIndex) {
    [aIndex, bIndex] = [bIndex, aIndex];
  }
  return (getPointNameSmall(aIndex) + getPointNameSmall(bIndex)) as SunSailNetSides;
}

export function getPoleId(poleName: PointName): PoleId {
  const pn = poleName.toUpperCase()
  switch (pn) {
    case "A":
      return PoleIndex.A;
    case "B":
      return PoleIndex.B;
    case "C":
      return PoleIndex.C;
    case "D":
      return PoleIndex.D;
    case "E":
      return PoleIndex.E;
    case "F":
      return PoleIndex.F;
  }
  return (pn.charCodeAt(0) - "A".charCodeAt(0)) as PoleId;
}

export function getEdgePoleIds(edgeName: EdgeName): PoleId[] {
  return [
    getPoleId(edgeName[0].toUpperCase() as PointName),
    getPoleId(edgeName[1].toUpperCase() as PointName),
  ]
}

export function getCornerTypeNameFormFinder(cornerType: CornerType | undefined): string {
  switch (cornerType) {
    case CornerType.D_Ring25:
      return "D-Ring Gurtband 25";
    case CornerType.D_Ring40:
      return "D-Ring Gurtband 40";
    case CornerType.RoundPlate:
      return "Rundplatte";
    case CornerType.OrcaPlate:
      return "Orca-Segelplatte";
    case CornerType.ChillSail:
      return "Chill Sail";
    case CornerType.O_Ring:
      return "O-Ring";
    case CornerType.OrcaRing:
      return "Orca-Desginring";
    case CornerType.BigPlate:
      return "Segelplatte groß";
    case CornerType.Strap25:
      return "Schlaufe Gurtband 25";
    case CornerType.Strap40:
      return "Schlaufe Gurtband 40";
    case CornerType.None:
    case undefined:
      return "None";
  }
  return "Unknown";
}

export function getOfferListForCornerArticle(articleId: string): OfferListType {
  switch (articleId) {
    case "00E13001637":
    case "00E13001638":
    case "90SOL0100":
    case "90SOL0103":
      return "KS";
  }
  return "S";
}

const cornerToArticle: Record<CornerType, string> = {
  [CornerType.BigPlate]: "00E13001416",
  [CornerType.ChillSail]: "90SOL0102",
  [CornerType.D_Ring25]: "00E13001637",
  [CornerType.D_Ring40]: "00E13001638",
  [CornerType.RoundPlate]: "00E13001418",
  [CornerType.O_Ring]: "00E13001417",
  [CornerType.OrcaRing]: "00E13001951",
  [CornerType.OrcaPlate]: "00E13001665",
  [CornerType.Strap25]: "90SOL0100",
  [CornerType.Strap40]: "90SOL0103",
  [CornerType.None]: "",
}
export function getArticleForCornerType(cornerType: CornerType | undefined): string {
  if (!cornerType || cornerType === CornerType.None)
    return "";
  if (cornerToArticle === undefined)
    throw new Error("Invalid corner Type!");
  return cornerToArticle[cornerType];
}

const articleToCorner = inverseRecord(cornerToArticle, (item) => item);

export function getCornerTypeForArticle(articleId: string | undefined): CornerType | undefined {
  if (!articleId)
    return undefined;
  return articleToCorner[articleId];
}

export function getConcavityName(c: ConcavityType): string {
  switch (c) {
    case "none":
      return "-";
    case "straight":
      return "gerade";
    case "concave":
      return "konkav";
  }
  return "?";
}

export function getMeasureOptions(measureOptions?: Partial<ISvgMeasureOptions>): ISvgMeasureOptions {
  return {
    sailMeasures: true,
    distOut: true,
    sailMountMeasures: false,
    distIn: false,
    diagonals: false,
    infoOverlay: false,
    centerPointTriangulation: false,
    ...measureOptions
  }
}

export function getCustomFiles(notesAndFiles?: INotesAndFiles): string[] | undefined {
  if (!notesAndFiles)
    return undefined;
  if ((notesAndFiles as any).customFiles) {
    const merged: string[] = [];
    merged.push(...(notesAndFiles as any).customFiles);
    if (notesAndFiles.files)
      merged.push(...notesAndFiles.files);
    return [...new Set(merged)];
  }
  return notesAndFiles.files;
}

export function getCustomFilesSafe(notesAndFiles?: INotesAndFiles): string[] {
  return getCustomFiles(notesAndFiles) || [];
}

export function isUserFile(source: string | ImageSource | undefined): source is UserFileImageSource {
  return !!(source && typeof source !== "string" && source?.type === "userFile")
}

export function getMaterialSettings(ms: IMaterialSettings | string | undefined): IMaterialSettings {
  if (typeof ms === "string") {
    return materialSettings[ms as MaterialSettingIds] || materialSettings.default;
  }
  if (ms)
    return ms;
  return materialSettings.default;
}

export function getInitialMaterial(imageSource: ImageSource | undefined): string | IMaterialSettings {
  if (imageSource?.type === "predefined")
    return "original";
  return {};
}

export function toArticleAvailability(available: boolean | undefined, longerDelivery: boolean | undefined): ArticleAvailability {
  const av = available === undefined ? true : available;
  return av ? (longerDelivery ? "longerDelivery" : "available") : "unavailable";
}

export function isRingschraubenArticleId(articleId: string): boolean {
  return ringschraubenArticleIds.includes(articleId);
}
export function isGewindeStangeArticle(articleId: string): boolean {
  return gewindeStangenArticleIds.includes(articleId);
}

export function getSunSailTypeForShortName(shortName: SunSailTypeNameShort): SunSailType | undefined {
  const sst = sailTypeByShortName[shortName];
  return sst !== undefined ? (+sst as SunSailType) : undefined;
}
